import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { RefreshCw, Search, Calendar, ChevronDown, X, Server } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { useTenantScope } from '../context/TenantScopeContext';
import TenantLink from '../components/TenantLink';
import { ColumnDef, DraggableTh, useColumnPrefs } from '../components/ColumnPicker';
import TenantSelect from '../components/TenantSelect';

interface Event {
  tenant_id: number;
  tenant_name?: string;
  received_at: string;
  source: string;
  host: string;
  host_label?: string | null;
  message: string;
  raw: string;
}

const EVENT_COLUMNS: ColumnDef[] = [
  { key: 'tenant', label: 'Tenant', defaultVisible: true },
  { key: 'received', label: 'Received', defaultVisible: true },
  { key: 'source', label: 'Source', defaultVisible: true },
  { key: 'host', label: 'Host', defaultVisible: true },
  { key: 'message', label: 'Message', defaultVisible: true },
];

const PAGE_SIZE = 200;
const AUTO_REFRESH_SECONDS = 300;
// Valori scritti da ingestion-gateway.ts (source) e jobs/lecsSync.ts ('ndr').
const SOURCES = ['syslog', 'cef', 'firewall', 'windows', 'dhcp', 'edr', 'ndr'];

// Vedi CasesPage.tsx: valore per <input type="datetime-local"> di N ore fa.
function defaultSince(hours: number): string {
  const d = new Date(Date.now() - hours * 3_600_000);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// ClickHouse restituisce timestamp UTC senza timezone: il suffisso Z li fa convertire in ora locale.
const fmt = (iso: string) => new Date(iso.replace(' ', 'T') + 'Z').toLocaleString('en-GB');

// Log identici (stesso tenant, host e messaggio) raggruppati in una riga con il contatore delle
// occorrenze (26/10/2026): firewall e domain controller ripetono lo stesso evento centinaia di
// volte e la griglia ne mostrava solo poche righe utili. Si raggruppano le righe caricate.
interface EventGroup { key: string; latest: Event; items: Event[] }

function groupEvents(events: Event[], enabled: boolean): EventGroup[] {
  if (!enabled) return events.map((e, i) => ({ key: String(i), latest: e, items: [e] }));
  const byKey = new Map<string, EventGroup>();
  for (const e of events) {
    const key = `${e.tenant_id}|${e.host}|${e.message}`;
    const g = byKey.get(key);
    if (g) g.items.push(e); else byKey.set(key, { key, latest: e, items: [e] });
  }
  return [...byKey.values()];
}


// Log di sicurezza per il tenant selezionato, oppure di tutti i tenant accessibili quando lo
// scope corrente è "All tenants" (vedi selettore in sidebar). Fino al 06/10/2026 la pagina
// chiedeva solo gli ultimi 100 eventi, senza ricerca né date: si vedevano sempre e solo i log
// più recenti. Ora: ricerca testuale (host, messaggio, raw), filtro sorgente, intervallo di
// date e "Load more" a cursore. Dal 26/10/2026: ultime 24 ore di default, refresh automatico
// ogni 5 minuti, e ?q= precompila la ricerca (link dalla ricerca globale dell'header).
export default function EventsExplorerPage() {
  const { selectedTenantId } = useTenantScope();
  const [searchParams] = useSearchParams();
  const { orderedDefs, moveColumn } = useColumnPrefs('soc.columns.events', EVENT_COLUMNS);
  const shownColumns = orderedDefs.filter(d => d.key !== 'tenant' || !selectedTenantId);
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState(() => searchParams.get('q') ?? '');
  const [source, setSource] = useState('');
  const [fromDate, setFromDate] = useState(() => defaultSince(24));
  const [toDate, setToDate] = useState('');
  const [hasMore, setHasMore] = useState(false);
  const [nextBefore, setNextBefore] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [openOccurrences, setOpenOccurrences] = useState<string | null>(null);
  const [grouped, setGrouped] = useState(true);
  const groups = useMemo(() => groupEvents(events, grouped), [events, grouped]);

  function params(before?: string | null) {
    return {
      tenantId: selectedTenantId ?? undefined,
      limit: PAGE_SIZE,
      search: search.trim() || undefined,
      source: source || undefined,
      fromDate: fromDate ? new Date(fromDate).toISOString() : undefined,
      toDate: toDate ? new Date(toDate).toISOString() : undefined,
      before: before || undefined,
    };
  }

  async function load() {
    setLoading(true);
    setError(null);
    setExpanded(null);
    try {
      const res = await axios.get('/api/events', { params: params() });
      setEvents(res.data.data);
      setHasMore(!!res.data.meta?.hasMore);
      setNextBefore(res.data.meta?.nextBefore ?? null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error while loading events');
    } finally {
      setLoading(false);
    }
  }

  async function loadMore() {
    if (!nextBefore) return;
    setLoadingMore(true);
    try {
      const res = await axios.get('/api/events', { params: params(nextBefore) });
      setEvents(prev => [...prev, ...res.data.data]);
      setHasMore(!!res.data.meta?.hasMore);
      setNextBefore(res.data.meta?.nextBefore ?? null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error while loading more events');
    } finally {
      setLoadingMore(false);
    }
  }

  useEffect(() => { load(); }, [selectedTenantId, source, fromDate, toDate]);

  // Ricerca testuale con debounce leggero per non interrogare ad ogni battitura.
  useEffect(() => {
    const timeout = setTimeout(load, 350);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // Arrivando da un'altra ricerca globale mentre si è già sulla pagina, la query cambia senza
  // rimontare il componente.
  useEffect(() => {
    const q = searchParams.get('q');
    if (q !== null) setSearch(q);
  }, [searchParams]);

  useEffect(() => {
    const interval = setInterval(load, AUTO_REFRESH_SECONDS * 1000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTenantId, source, fromDate, toDate, search]);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-xl font-semibold text-gray-900">Security Logs</h1>
          <TenantSelect />
        </div>
        <button onClick={load} title="Refreshes automatically every 5 minutes" disabled={loading} className="btn-secondary flex items-center gap-1 disabled:opacity-50">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      <div className="card p-3 mb-4 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[16rem]">
          <Search size={14} className="absolute left-2.5 top-2.5 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search in host, message or raw log…"
            className="input-field w-full pl-8" />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-2 top-2.5 text-gray-400 hover:text-gray-600" aria-label="Clear search"><X size={14} /></button>
          )}
        </div>
        <select value={source} onChange={e => setSource(e.target.value)} className="input-field w-40">
          <option value="">All sources</option>
          {SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <div className="flex items-center gap-1.5">
          <Calendar size={14} className="text-gray-400" />
          <input type="datetime-local" value={fromDate} onChange={e => setFromDate(e.target.value)} className="input-field w-auto py-1" />
          <span className="text-xs text-gray-400">to</span>
          <input type="datetime-local" value={toDate} onChange={e => setToDate(e.target.value)} className="input-field w-auto py-1" />
          {(fromDate || toDate) && (
            <button onClick={() => { setFromDate(''); setToDate(''); }} className="text-xs text-primary-600 hover:underline ml-1">Any time</button>
          )}
        </div>
        <label className="flex items-center gap-1.5 text-sm text-gray-600 cursor-pointer select-none"
          title="One row per identical log (same tenant, host and message), with the number of occurrences">
          <input type="checkbox" checked={grouped} onChange={e => setGrouped(e.target.checked)} />
          Group identical
        </label>
      </div>

      {error && <div className="mb-4 text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</div>}

      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr>
              {shownColumns.map(d => (
                <DraggableTh key={d.key} colKey={d.key} onMove={moveColumn} className="table-header">
                  {d.label}
                </DraggableTh>
              ))}
              <th className="table-header w-px" aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td className="table-cell text-gray-400" colSpan={shownColumns.length + 1}>Loading…</td></tr>}
            {!loading && events.length === 0 && (
              <tr><td className="table-cell text-gray-400" colSpan={shownColumns.length + 1}>
                {search || source || fromDate || toDate
                  ? 'No event matches the current filters — widen the date range or clear the search.'
                  : 'No event received yet. Check that at least one broker is active and sending data.'}
              </td></tr>
            )}
            {!loading && groups.map(({ key, latest: e, items }) => (
              <tr key={key} className="group border-t border-gray-100 hover:bg-gray-50 cursor-pointer align-top"
                onClick={() => setExpanded(expanded === key ? null : key)} title="Click to show the raw log">
                {shownColumns.map(d => (
                  <td key={d.key} className={`table-cell ${d.key === 'received' ? 'whitespace-nowrap text-gray-500' : ''} ${d.key === 'tenant' ? 'text-gray-500' : ''} ${d.key === 'message' ? 'font-mono text-xs' : ''}`}>
                    {d.key === 'tenant' && <TenantLink tenantId={e.tenant_id} name={e.tenant_name} />}
                    {d.key === 'received' && (
                      <>
                        {fmt(e.received_at)}
                        {items.length > 1 && (
                          <button onClick={ev => { ev.stopPropagation(); setOpenOccurrences(openOccurrences === key ? null : key); }}
                            title={`${items.length} identical logs: show when they arrived`}
                            className="ml-2 inline-flex items-center gap-0.5 rounded-full bg-primary-600 px-1.5 text-[11px] font-semibold text-white hover:bg-primary-700">
                            ×{items.length}<ChevronDown size={11} className={openOccurrences === key ? 'rotate-180' : ''} />
                          </button>
                        )}
                        {openOccurrences === key && (
                          <ul className="mt-1.5 max-h-48 overflow-y-auto text-[11px] text-gray-500 space-y-0.5" onClick={ev => ev.stopPropagation()}>
                            {items.map((o, i) => <li key={i}>{fmt(o.received_at)}</li>)}
                          </ul>
                        )}
                      </>
                    )}
                    {d.key === 'source' && <span className="badge bg-primary-50 text-primary-700">{e.source}</span>}
                    {d.key === 'host' && (
                      <>
                        {e.host_label || e.host}
                        {e.host_label && <span className="text-gray-400"> · {e.host}</span>}
                      </>
                    )}
                    {d.key === 'message' && (
                      <>
                        {e.message}
                        {expanded === key && e.raw && e.raw !== e.message && (
                          <pre className="mt-2 p-2 bg-gray-50 border border-gray-100 rounded text-[11px] whitespace-pre-wrap break-all text-gray-600">{e.raw}</pre>
                        )}
                      </>
                    )}
                  </td>
                ))}
                {/* Analisi rapida: cerca lo stesso messaggio o lo stesso host in tutto il periodo.
                    Visibili al passaggio del mouse o con il fuoco. */}
                <td className="table-cell whitespace-nowrap text-right" onClick={ev => ev.stopPropagation()}>
                  <span className="inline-flex gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100">
                    <button onClick={() => setSearch(e.message.slice(0, 120))} title="Search logs with this message"
                      className="p-1 rounded text-gray-500 hover:text-primary-700 hover:bg-primary-50"><Search size={14} /></button>
                    <button onClick={() => setSearch(e.host)} title={`Search logs from ${e.host}`}
                      className="p-1 rounded text-gray-500 hover:text-primary-700 hover:bg-primary-50"><Server size={14} /></button>
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!loading && events.length > 0 && (
          <div className="px-4 py-2 border-t border-gray-100 text-xs text-gray-500 flex items-center gap-3">
            <span>
              {events.length} event{events.length === 1 ? '' : 's'} shown{grouped && groups.length < events.length ? ` in ${groups.length} distinct rows` : ''}{hasMore ? ', more available' : ''}.
            </span>
            {hasMore && (
              <button onClick={loadMore} disabled={loadingMore} className="inline-flex items-center gap-1 text-primary-600 hover:underline disabled:opacity-50">
                <ChevronDown size={13} /> {loadingMore ? 'Loading…' : `Load ${PAGE_SIZE} more`}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
